/**
 * Made to Order Dialog Handler - Premium Implementation
 * Handles elegant dialog for sold-out items with WhatsApp redirect
 * Uses WhatsApp API: https://api.whatsapp.com/send
 *
 * DIAGNOSTIC VERSION - Enhanced logging for troubleshooting
 */

(function() {
  'use strict';

  // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  // console.log('🚀 Made to Order Dialog: Script Loading...');
  // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  // ========================================
  // CONFIGURATION
  // ========================================
  const CONFIG = {
    // WhatsApp business number (with country code, no + or 00)
    // Example: For +91 6360580124, use: 916360580124
    whatsappNumber: '916360580124',  // ← UPDATE THIS

    // Store name
    storeName: 'Reia Diamonds',

    // Deposit percentage
    depositPercentage: 50
  };

  // console.log('⚙️  CONFIG:', CONFIG);

  /**
   * Single source of truth for "should this variant show Made to Order?".
   * Made to Order when:
   *  - there is no variant, OR
   *  - the variant is unavailable (inventory policy "deny" + out of stock), OR
   *  - inventory is tracked and quantity is 0 or less (inventory policy
   *    "continue selling when out of stock" — here variant.available stays true
   *    at 0 stock, so we must check the quantity directly).
   */
  function isMadeToOrder(variant) {
    if (!variant) return true;
    if (variant.available === false) return true;
    return variant.inventory_management != null && variant.inventory_quantity <= 0;
  }

  class MadeToOrderDialog {
    constructor() {
      // console.log('🏗️  Constructing MadeToOrderDialog instance...');
      this.overlay = null;
      this.dialog = null;
      this.closeBtn = null;
      this.whatsappBtn = null;
      this.currentVariant = null;

      this.init();
    }

    init() {
      // console.log('🔄 Initializing MadeToOrderDialog...');
      // console.log('   Document readyState:', document.readyState);

      if (document.readyState === 'loading') {
        // console.log('   ⏳ Document still loading, waiting for DOMContentLoaded...');
        document.addEventListener('DOMContentLoaded', () => {
          // console.log('   ✅ DOMContentLoaded fired, calling setup()...');
          this.setup();
        });
      } else {
        // console.log('   ✅ Document already loaded, calling setup() immediately...');
        this.setup();
      }
    }

    setup() {
      // console.log('\n📋 SETUP PHASE START');
      // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Get dialog elements
      // console.log('🔍 Step 1: Looking for dialog elements...');
      this.overlay = document.querySelector('[data-mto-overlay]');
      this.dialog = document.querySelector('[data-mto-dialog]');
      this.closeBtn = document.querySelector('[data-mto-close]');
      this.whatsappBtn = document.querySelector('[data-mto-whatsapp-btn]');

      // console.log('   Dialog overlay found:', !!this.overlay);
      // console.log('   Dialog element found:', !!this.dialog);
      // console.log('   Close button found:', !!this.closeBtn);
      // console.log('   WhatsApp button found:', !!this.whatsappBtn);

      if (!this.overlay || !this.dialog) {
        console.error('❌ CRITICAL: Dialog elements not found! Cannot initialize.');
        console.error('   Missing overlay:', !this.overlay);
        console.error('   Missing dialog:', !this.dialog);
        return;
      }

      // console.log('✅ Step 1 Complete: Dialog elements found\n');

      // Set up event listeners
      // console.log('🔍 Step 2: Setting up event listeners...');
      this.setupEventListeners();
      // console.log('✅ Step 2 Complete: Event listeners set up\n');

      // Intercept button clicks
      // console.log('🔍 Step 3: Intercepting Made to Order buttons...');
      this.interceptButtons();
      // console.log('✅ Step 3 Complete: Buttons intercepted\n');

      // Initialize Made to Order buttons visibility on page load
      // console.log('🔍 Step 4: Initializing button visibility...');
      this.initializeButtonVisibility();
      // console.log('✅ Step 4 Complete: Button visibility initialized\n');

      // Listen for variant changes
      // console.log('🔍 Step 5: Setting up variant change listener...');
      this.setupVariantChangeListener();
      // console.log('✅ Step 5 Complete: Variant change listener set up\n');

      // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // console.log('✅ SETUP PHASE COMPLETE - Made to Order Dialog Ready');
      // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }

    setupEventListeners() {
      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
        // console.log('   ✓ Close button listener added');
      } else {
        console.warn('   ⚠️  No close button found');
      }

      // Overlay click (backdrop)
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.close();
        }
      });
      // console.log('   ✓ Overlay click listener added');

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });
      // console.log('   ✓ Escape key listener added');

      // WhatsApp button
      if (this.whatsappBtn) {
        this.whatsappBtn.addEventListener('click', () => this.openWhatsApp());
        // console.log('   ✓ WhatsApp button listener added');
      } else {
        console.warn('   ⚠️  No WhatsApp button found');
      }
    }

    interceptButtons() {
      // Use event delegation for [data-made-to-order-btn] so dynamically-loaded
      // buttons (e.g. quick view on collection pages) are handled automatically.
      document.addEventListener('click', (e) => {
        const button = e.target.closest('[data-made-to-order-btn]');
        if (!button) return;
        e.preventDefault();
        e.stopPropagation();

        const variantDetails = this.getVariantDetailsFromButton(button);

        if (variantDetails) {
          this.open(variantDetails);
        } else {
          console.error('   Failed to get variant details from button');
          alert('Unable to get product details. Please try again.');
        }
      });

      // Intercept Sticky Cart "Made to Order" button
      const stickyMTOButton = document.querySelector('[data-sticky-made-to-order-btn]');
      if (stickyMTOButton) {
        // console.log('   Found sticky cart Made to Order button');
        stickyMTOButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // console.log('\n🖱️  Sticky cart Made to Order button clicked!');

          const variantDetails = this.getVariantDetailsFromButton(stickyMTOButton);

          if (variantDetails) {
            // console.log('   ✅ Variant details retrieved:', variantDetails);
            this.open(variantDetails);
          } else {
            console.error('   ❌ Failed to get variant details from sticky button');
            alert('Unable to get product details. Please try again.');
          }
        });
        // console.log('   ✓ Sticky cart button listener added');
      } else {
        // console.log('   ℹ️  No sticky cart Made to Order button found (this is OK)');
      }

      // Intercept drawer "Made to Order" button
      const drawerMTOButton = document.querySelector('[data-drawer-made-to-order-btn]');
      if (drawerMTOButton) {
        // console.log('   Found drawer Made to Order button');
        drawerMTOButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // console.log('\n🖱️  Drawer Made to Order button clicked!');

          let variantDetails = null;

          // Try to get variant from drawer instance
          if (window.customizeDrawer) {
            const variant = window.customizeDrawer.getCurrentVariant();
            if (variant) {
              variantDetails = {
                productName: this.getProductName(),
                variantId: variant.id,
                variantTitle: variant.title,
                variantPrice: variant.price,
                productUrl: window.location.href
              };
            }
          }

          // Fallback: Try button data attributes
          if (!variantDetails) {
            variantDetails = this.getVariantDetailsFromButton(drawerMTOButton);
          }

          if (variantDetails) {
            // console.log('   ✅ Variant details retrieved:', variantDetails);
            this.open(variantDetails);

            // Close drawer after a short delay
            setTimeout(() => {
              if (window.customizeDrawer) {
                window.customizeDrawer.close();
              }
            }, 100);
          } else {
            console.error('   ❌ Could not find variant details');
            alert('Unable to get product details. Please try again.');
          }
        });
        // console.log('   ✓ Drawer button listener added');
      } else {
        // console.log('   ℹ️  No drawer Made to Order button found (this is OK)');
      }
    }

    getVariantDetailsFromButton(button) {
      // Try data attributes first
      const variantId = button.getAttribute('data-variant-id');
      const variantTitle = button.getAttribute('data-variant-title');
      const variantPrice = button.getAttribute('data-variant-price');

      // Use data-product-name/data-product-url from button if available (quick view),
      // otherwise fall back to page-level helpers.
      const productName = button.getAttribute('data-product-name') || this.getProductName();
      const productUrl = button.getAttribute('data-product-url') || window.location.href;

      if (variantId && variantTitle && variantPrice) {
        return {
          productName: productName,
          variantId: variantId,
          variantTitle: variantTitle,
          variantPrice: parseInt(variantPrice),
          productUrl: productUrl
        };
      }

      // Fallback: get from form
      const form = button.closest('form');
      if (form) {
        const variantSelect = form.querySelector('select[data-variant-select]');
        if (variantSelect) {
          const selectedOption = variantSelect.options[variantSelect.selectedIndex];
          const selectedVariantId = selectedOption.value;
          const variant = this.getVariantData(selectedVariantId);

          if (variant) {
            return {
              productName: productName,
              variantId: variant.id,
              variantTitle: variant.title,
              variantPrice: variant.price,
              productUrl: productUrl
            };
          }
        }
      }

      return null;
    }

    getProductName() {
      const productNameEl = document.querySelector('.product__title') ||
                           document.querySelector('[data-product-title]') ||
                           document.querySelector('h1');

      return productNameEl ? productNameEl.textContent.trim() : 'Product';
    }

    getVariantData(variantId) {
      if (window.productVariants) {
        return window.productVariants.find(v => v.id.toString() === variantId.toString());
      }

      const variantDataEl = document.querySelector('[data-drawer-variant-json]');
      if (variantDataEl) {
        try {
          const variants = JSON.parse(variantDataEl.textContent);
          return variants.find(v => v.id.toString() === variantId.toString());
        } catch (e) {
          console.error('Failed to parse variant data', e);
        }
      }

      return null;
    }

    open(variantDetails) {
      this.currentVariant = variantDetails;

      // Populate dialog
      this.populateDialog(variantDetails);

      // Show dialog
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Dispatch custom event for analytics tracking
      document.dispatchEvent(new CustomEvent('mto:dialog-opened', { detail: variantDetails }));

      // console.log('✅ Made to Order Dialog opened:', variantDetails);
    }

    close() {
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // console.log('✅ Made to Order Dialog closed');
    }

    populateDialog(details) {
      const { productName, variantTitle, variantPrice } = details;

      // Product name
      const productNameEl = document.querySelector('[data-mto-product-name]');
      if (productNameEl) {
        productNameEl.textContent = productName;
      }

      // Variant title - filter out metal weight
      const variantTitleEl = document.querySelector('[data-mto-variant-title]');
      if (variantTitleEl) {
        // Remove metal weight pattern (e.g., "2.5 gm", "3.25gm", "2 g", etc.)
        const cleanedTitle = variantTitle.replace(/\s*\/?\s*\d+\.?\d*\s*(gm|g|gram|grams)\s*/gi, '').trim();
        variantTitleEl.textContent = cleanedTitle;
      }

      // Full price
      const fullPriceEl = document.querySelector('[data-mto-full-price]');
      if (fullPriceEl) {
        fullPriceEl.textContent = this.formatPrice(variantPrice);
      }

      // Deposit amount (50%)
      const depositAmountEl = document.querySelector('[data-mto-deposit-amount]');
      if (depositAmountEl) {
        const depositAmount = (variantPrice * CONFIG.depositPercentage) / 100;
        depositAmountEl.textContent = this.formatPrice(depositAmount);
      }
    }

    openWhatsApp() {
      if (!this.currentVariant) {
        console.error('No variant data');
        return;
      }

      const { productName, variantTitle, variantPrice, productUrl } = this.currentVariant;

      // Calculate deposit
      const depositAmount = (variantPrice * CONFIG.depositPercentage) / 100;

      // Remove metal weight from variant title for WhatsApp message
      const cleanedVariantTitle = variantTitle.replace(/\s*\/?\s*\d+\.?\d*\s*(gm|g|gram|grams)\s*/gi, '').trim();

      // Build WhatsApp message
      const message = this.buildWhatsAppMessage({
        productName,
        variantTitle: cleanedVariantTitle,
        fullPrice: this.formatPrice(variantPrice),
        depositAmount: this.formatPrice(depositAmount),
        depositPercentage: CONFIG.depositPercentage,
        productUrl
      });

      // Build WhatsApp URL using the API format
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${CONFIG.whatsappNumber}&text=${encodedMessage}`;

      // console.log('📱 Opening WhatsApp:', whatsappUrl);

      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank');

      // Close dialog
      this.close();
    }

    buildWhatsAppMessage(data) {
      const lines = [
        `Hello,`,
        `I would like to order a bespoke piece from ${CONFIG.storeName}.`,
        ``,
        `Product: ${data.productName}`,
        `Variant: ${data.variantTitle}`,
        `Total Price: ${data.fullPrice}`,
        ``,
        `I understand this is a made-to-order item and I am ready to pay ${data.depositPercentage}% advance payment of ${data.depositAmount} to begin production.`,
        ``,
        `Product Link: ${data.productUrl}`,
        ``,
        `Could you please give me further details about:`,
        `- Production timeline`,
        `- Payment process for the advance`,
        `- Customization options (if any)`,
        `- Balance payment and shipping`,
        ``,
        `Thank You!`
      ];

      return lines.join('\n');
    }

    formatPrice(price) {
      // Price comes in cents, convert to rupees
      const priceInRupees = price / 100;

      const formatted = priceInRupees.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return `Rs. ${formatted}`;
    }

    initializeButtonVisibility() {
      // console.log('\n🔍 INITIALIZE BUTTON VISIBILITY');
      // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Check all "Made to Order" buttons on page load and show if needed
      const madeToOrderButtons = document.querySelectorAll('[data-made-to-order-btn]');
      // console.log('📊 Found', madeToOrderButtons.length, 'Made to Order buttons');

      if (madeToOrderButtons.length === 0) {
        return;
      }

      madeToOrderButtons.forEach((mtoBtn, index) => {
        // console.log(`\n📍 Processing button ${index + 1}/${madeToOrderButtons.length}`);

        // Check if this button is in a wrapper (main product page)
        const wrapper = mtoBtn.closest('.product-mto-button-wrapper');
        // console.log('   Wrapper element:', wrapper);
        // console.log('   Has wrapper:', !!wrapper);

        if (wrapper) {
          // console.log('   📦 Main product page button (has wrapper)');

          // Try multiple selectors for Add to Cart button
          const addToCartBtn = document.querySelector('[data-add-to-cart]') ||
                              document.querySelector('[name="add"]') ||
                              document.querySelector('.product-form__cart-submit');

          // console.log('   🛒 Add to Cart button:', addToCartBtn);
          // console.log('      - Found:', !!addToCartBtn);

          if (addToCartBtn) {
            if (addToCartBtn.disabled) {
              wrapper.style.setProperty('display', 'block', 'important');
              addToCartBtn.style.setProperty('display', 'none', 'important');
            } else {
              wrapper.style.setProperty('display', 'none', 'important');
              addToCartBtn.style.removeProperty('display');
            }
          } else {
            console.error('MTO: Add to Cart button not found');
          }
        } else {
          // console.log('   📄 Other button (drawer, etc - no wrapper)');

          const addToCartBtn = mtoBtn.parentElement.querySelector('[data-add-to-cart]');
          // console.log('   🛒 Add to Cart button:', addToCartBtn);
          // console.log('      - Found:', !!addToCartBtn);

          if (addToCartBtn && addToCartBtn.disabled) {
            // console.log('   ✅ Showing button (sold out)');
            mtoBtn.style.setProperty('display', 'block', 'important');
          } else {
            // console.log('   ❌ Hiding button (in stock or no ATC button)');
            mtoBtn.style.setProperty('display', 'none', 'important');
          }
        }
      });

      // Handle Sticky Cart Made to Order Button
      // console.log('\n📍 Processing Sticky Cart Made to Order Button');
      const stickyMTOBtn = document.querySelector('[data-sticky-made-to-order-btn]');
      const stickyAddToCartBtn = document.querySelector('.sticky-atc-bar [data-add-to-cart]');

      if (stickyMTOBtn && stickyAddToCartBtn) {
        // console.log('   Found sticky cart buttons');
        // console.log('   Sticky Add to Cart disabled:', stickyAddToCartBtn.disabled);

        if (stickyAddToCartBtn.disabled) {
          // console.log('   ✅ SOLD OUT - Showing sticky MTO button');
          stickyMTOBtn.style.setProperty('display', 'block', 'important');
          stickyAddToCartBtn.style.setProperty('display', 'none', 'important');
        } else {
          // console.log('   ❌ IN STOCK - Hiding sticky MTO button');
          stickyMTOBtn.style.setProperty('display', 'none', 'important');
          stickyAddToCartBtn.style.removeProperty('display');
        }
      } else {
        // console.log('   ℹ️  Sticky cart buttons not found (this is OK if sticky cart is disabled)');
      }

      // console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // console.log('✅ Button visibility initialization complete');
      // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }

    setupVariantChangeListener() {
      // console.log('   Setting up variant change event listener...');

      // Listen for the custom variant change event dispatched by theme.js
      document.addEventListener('product:variant-change', (event) => {
        // console.log('\n🔔 VARIANT CHANGE EVENT RECEIVED');
        // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        // console.log('📦 Event detail:', event.detail);

        const variant = event.detail.variant;

        if (variant) {
          // console.log('📊 Variant Information:');
          // console.log('   - ID:', variant.id);
          // console.log('   - Title:', variant.title);
          // console.log('   - Available:', variant.available);
          // console.log('   - Price:', variant.price);
          // console.log('   - Full variant object:', variant);
        } else {
          // console.log('⚠️  No variant in event (variant is null/undefined)');
        }

        // Update all Made to Order buttons based on variant availability
        const madeToOrderButtons = document.querySelectorAll('[data-made-to-order-btn]');
        // console.log('\n🔄 Updating', madeToOrderButtons.length, 'Made to Order buttons...\n');

        madeToOrderButtons.forEach((mtoBtn, index) => {
          // console.log(`📍 Button ${index + 1}/${madeToOrderButtons.length}`);

          // Check if this button is in a wrapper (main product page)
          const wrapper = mtoBtn.closest('.product-mto-button-wrapper');
          // console.log('   Has wrapper:', !!wrapper);

          // Determine if we should show or hide based on stock level
          // (works for both "deny" and "continue selling" inventory policies)
          const shouldShow = isMadeToOrder(variant);

          // console.log('   Decision Logic:');
          // console.log('      - Variant exists:', !!variant);
          // console.log('      - Variant available:', variant ? variant.available : 'N/A');
          // console.log('      - Should show MTO:', shouldShow);

          if (shouldShow) {
            if (wrapper) {
              wrapper.style.setProperty('display', 'block', 'important');
              // Explicitly hide the main ATC button so it can't show alongside MTO
              const mainAtcBtn = wrapper.closest('form') ?
                wrapper.closest('form').querySelector('[data-add-to-cart]') :
                document.querySelector('.product-form__controls-group [data-add-to-cart]');
              if (mainAtcBtn) mainAtcBtn.style.setProperty('display', 'none', 'important');
            } else {
              mtoBtn.style.setProperty('display', 'block', 'important');
            }

            // Update button data attributes with variant info
            if (variant) {
              mtoBtn.setAttribute('data-variant-id', variant.id);
              mtoBtn.setAttribute('data-variant-title', variant.title);
              mtoBtn.setAttribute('data-variant-price', variant.price);
            }
          } else {
            if (wrapper) {
              wrapper.style.setProperty('display', 'none', 'important');
              // Restore ATC button visibility (let theme.js/disabled+CSS control its state)
              const mainAtcBtn = wrapper.closest('form') ?
                wrapper.closest('form').querySelector('[data-add-to-cart]') :
                document.querySelector('.product-form__controls-group [data-add-to-cart]');
              if (mainAtcBtn) mainAtcBtn.style.removeProperty('display');
            } else {
              mtoBtn.style.setProperty('display', 'none', 'important');
            }
          }

          // console.log(''); // Blank line between buttons
        });

        // Handle Sticky Cart Made to Order Button on variant change
        // console.log('\n📍 Updating Sticky Cart Made to Order Button');
        const stickyMTOBtn = document.querySelector('[data-sticky-made-to-order-btn]');
        const stickyAddToCartBtn = document.querySelector('.sticky-atc-bar [data-add-to-cart]');

        if (stickyMTOBtn && stickyAddToCartBtn) {
          const shouldShow = isMadeToOrder(variant);

          // console.log('   Decision Logic:');
          // console.log('      - Variant exists:', !!variant);
          // console.log('      - Variant available:', variant ? variant.available : 'N/A');
          // console.log('      - Should show sticky MTO:', shouldShow);

          if (shouldShow) {
            // console.log('   ✅ SHOWING sticky MTO button (variant sold out or null)');
            stickyMTOBtn.style.setProperty('display', 'block', 'important');
            stickyAddToCartBtn.style.setProperty('display', 'none', 'important');

            // Update button data attributes with variant info
            if (variant) {
              // console.log('      Updating sticky button data attributes...');
              stickyMTOBtn.setAttribute('data-variant-id', variant.id);
              stickyMTOBtn.setAttribute('data-variant-title', variant.title);
              stickyMTOBtn.setAttribute('data-variant-price', variant.price);
              // console.log('      ✓ Data attributes updated');
            }
          } else {
            // console.log('   ❌ HIDING sticky MTO button (variant available/in stock)');
            stickyMTOBtn.style.setProperty('display', 'none', 'important');
            stickyAddToCartBtn.style.removeProperty('display');
          }
        } else {
          // console.log('   ℹ️  Sticky cart buttons not found (this is OK if sticky cart is disabled)');
        }

        // console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        // console.log('✅ Variant change processing complete');
        // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
      });

      // console.log('   ✓ Variant change listener registered');
      // console.log('   Listening for: product:variant-change event');
    }
  }

  // Initialize
  // console.log('\n🚀 Creating MadeToOrderDialog instance...\n');
  window.madeToOrderDialog = new MadeToOrderDialog();
  // console.log('✅ window.madeToOrderDialog created and available globally\n');

})();
